import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { PaymentReceiptCreateStore } from "../../store";
import { FileCRUDType } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { PaymentReceiptTypeCreate } from "../../../paymentReceiptType/components/create/PaymentReceiptTypeCreate";
import { PaymentReceiptTypeCreateStore } from "../../../paymentReceiptType/store";
import { PaymentReceiptAmountCustomField } from "../custom/PaymentReceiptAmountCustomField";
import { InputDateTime } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type PaymentReceiptCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  paymentReceiptOnRestoreFile: (id: string) => void;
  paymentReceiptOnLoadFile: (file: any) => void;
  paymentReceiptOnCommitFile: (changes: GridChangeSet) => void;
  paymentReceiptFiles: FileCRUDType[];
  paymentReceiptError?: string;
  typeSelectedValue?: { displayId: string; id: string } | null;
  onTypeSelected?: (value: { displayId: string; id: string } | null) => void;
  onTypeChange?: (value: string) => void;
  typeOptions?: { displayId: string; id: string }[];
  typeError?: string;
  amountSelectedValue?: { displayId: string; id: string } | null;
  onAmountChange?: (value: number) => void;
  amount: number;
  amountError?: string;
  issueDateTimeSelectedValue?: { displayId: string; id: string } | null;
  onIssueDateTimeChange?: (value: Date | null) => void;
  issueDateTime: Date | null;
  issueDateTimeError?: string;
  dummyCRUDRestoredObservable: number;
  paymentReceiptCreateStore: PaymentReceiptCreateStore;
};

export const PaymentReceiptCreateForm: React.FC<PaymentReceiptCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      paymentReceiptOnRestoreFile,
      paymentReceiptOnLoadFile,
      paymentReceiptOnCommitFile,
      paymentReceiptFiles,
      paymentReceiptError,
      onTypeSelected = () => {},
      typeSelectedValue = null,
      typeOptions = [],
      typeError,
      onTypeChange = () => {},
      amount = 0,
      amountError,
      onAmountChange = () => {},
      issueDateTime = null,
      issueDateTimeError,
      onIssueDateTimeChange = () => {},
      dummyCRUDRestoredObservable,
      paymentReceiptCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              values={{ type: typeSelectedValue, amount, issueDateTime }}
            >
              <FormExtras
                entity="PaymentReceipt"
                mode="create"
                values={{ type: typeSelectedValue, amount, issueDateTime }}
                options={{ type: typeOptions }}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "type":
                      onTypeSelected?.(valor);
                      break;
                    case "amount":
                      onAmountChange?.(valor);
                      break;
                    case "issueDateTime":
                      onIssueDateTimeChange?.(valor);
                      break;
                  }
                }}
              />
              <GroupContainer
                id="null"
                errorText={paymentReceiptCreateStore.getCardErrors("null")}
                title={langStore.groupPaymentReceiptLabel}
                collapsible={false}
              >
                <FormControl fullWidth>
                  <LoadFilesCRUDCompact
                    rows={paymentReceiptFiles}
                    maxRows={1}
                    showAddCommand={true}
                    showDeleteCommand={true}
                    label={langStore.PaymentReceiptPaymentReceiptLabel}
                    description={
                      langStore.PaymentReceiptPaymentReceiptDescription
                    }
                    showLabel={true}
                    onLoadFile={(file: File) => paymentReceiptOnLoadFile(file)}
                    columns={[
                      { name: "mimetype", title: langStore.fileTypeLabel },
                      { name: "name", title: langStore.fileNameLabel },
                      { name: "formattedSize", title: langStore.fileSizeLabel },
                    ]}
                    onRestore={(id: string) => paymentReceiptOnRestoreFile(id)}
                    onCommitChanges={(changes: GridChangeSet) =>
                      paymentReceiptOnCommitFile(changes)
                    }
                    dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                    error={paymentReceiptError}
                  />
                  <InputReference
                    id="type"
                    label={langStore.PaymentReceiptTypeLabel}
                    autoFocus
                    error={Boolean(typeError)}
                    helperText={
                      typeError ||
                      paymentReceiptCreateStore.warnings["type"]?.message
                    }
                    selectedValue={typeSelectedValue}
                    onChange={(value: string) => onTypeChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onTypeSelected(value)}
                    options={typeOptions}
                    description={langStore.PaymentReceiptTypeDescription}
                    canCreate={rootStore.authStore.hasPermissions(
                      "paymentReceiptType_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new PaymentReceiptTypeCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <PaymentReceiptTypeCreate
                            paymentReceiptTypeCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewPaymentReceiptType"
                        cancelMethod="cancelNewPaymentReceiptType"
                      />
                    )}
                  />
                  <PaymentReceiptAmountCustomField
                    value={amount}
                    error={Boolean(amountError)}
                    helperText={
                      amountError ||
                      paymentReceiptCreateStore.warnings["amount"]?.message
                    }
                    {...{ optional: false }}
                    label={langStore.PaymentReceiptAmountLabel}
                    description={langStore.PaymentReceiptAmountDescription}
                    onChange={(value: number) => onAmountChange(value)}
                  />
                  <InputDateTime
                    value={issueDateTime}
                    error={Boolean(issueDateTimeError)}
                    warning={
                      !issueDateTimeError &&
                      Boolean(
                        paymentReceiptCreateStore.warnings["issueDateTime"]
                      )
                    }
                    helperText={
                      issueDateTimeError ||
                      paymentReceiptCreateStore.warnings["issueDateTime"]
                        ?.message
                    }
                    label={langStore.PaymentReceiptIssueDateTimeLabel}
                    nullText={langStore.optionalFieldHint}
                    description={
                      langStore.PaymentReceiptIssueDateTimeDescription
                    }
                    onChange={(value: Date | null | null) =>
                      onIssueDateTimeChange(value)
                    }
                  />
                </FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
